/**
 * Section B1 Page
 * B.1.方法論引用
 * 單筆富文本模式 (參照 SectionA1Page)
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { validateSession } from '../utils/sessionValidator';
import { usePermission } from '../hooks/usePermission';
import { useProject } from '../contexts/ProjectContext';
import sectionB1Service, { SectionB1Detail } from '../services/sectionB1Service';
import RichTextEditor from '../components/RichTextEditor';
import FunctionPageHeader from '../components/FunctionPageHeader';
import { useToast } from '../contexts/ToastContext';
import '../styles/DataTable.css';

const SectionB1Page: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { hasPermission, loading: permissionLoading } = usePermission();
  const { selectedProjectId } = useProject();
  const toast = useToast();
  const hasInitialized = useRef(false);

  // ========== 資料狀態 ==========
  const [sectionB1Data, setSectionB1Data] = useState<SectionB1Detail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // ========== 表單狀態 ==========
  const [formData, setFormData] = useState({
    mothed_references: ''
  });

  // ========== 原始資料（用於重置） ==========
  const [originalData, setOriginalData] = useState({
    mothed_references: ''
  });

  // ========== 初始化 ==========
  useEffect(() => {
    if (!hasInitialized.current && !permissionLoading) {
      validateSession('SectionB1Page');
      hasInitialized.current = true;
    }
  }, [permissionLoading]);

  // ========== 專案變更時載入資料 ==========
  useEffect(() => {
    if (!selectedProjectId) {
      setSectionB1Data(null);
      setFormData({ mothed_references: '' });
      setOriginalData({ mothed_references: '' });
      return;
    }

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        setSuccess(null);
        const data = await sectionB1Service.getSectionB1ByProject(selectedProjectId);
        if (data) {
          setSectionB1Data(data);
          const formValues = { mothed_references: data.mothed_references || '' };
          setFormData(formValues);
          setOriginalData(formValues);
        } else {
          setSectionB1Data(null);
          setFormData({ mothed_references: '' });
          setOriginalData({ mothed_references: '' });
        }
      } catch (err: any) {
        console.error('載入 B.1 資料失敗:', err);
        // 404 或 500（資料表尚未建立）視為無資料，不顯示錯誤
        if (err.response?.status === 404 || err.response?.status === 500) {
          setSectionB1Data(null);
          setFormData({ mothed_references: '' });
          setOriginalData({ mothed_references: '' });
        } else {
          setError(err.response?.data?.detail || (i18n.language === 'zh-TW' ? '載入資料失敗' : 'Failed to load data'));
        }
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [selectedProjectId, i18n.language]);

  // ========== 儲存 ==========
  const handleSave = async () => {
    if (!selectedProjectId) {
      setError(i18n.language === 'zh-TW' ? '請先在上方選擇專案' : 'Please select a project first');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const updatedData = await sectionB1Service.updateSectionB1(selectedProjectId, {
        mothed_references: formData.mothed_references || undefined
      });

      setSectionB1Data(updatedData);
      const formValues = { mothed_references: updatedData.mothed_references || '' };
      setOriginalData(formValues);
      setSuccess(i18n.language === 'zh-TW' ? '儲存成功' : 'Saved successfully');
      toast.success(i18n.language === 'zh-TW' ? '儲存成功' : 'Saved successfully');

    } catch (err: any) {
      console.error('儲存失敗:', err);
      const errorMsg = err.response?.data?.detail || (i18n.language === 'zh-TW' ? '儲存失敗' : 'Save failed');
      setError(errorMsg);
      toast.error(i18n.language === 'zh-TW' ? `儲存失敗: ${errorMsg}` : `Save failed: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  // ========== 重置 ==========
  const handleReset = () => {
    setFormData({ ...originalData });
    setError(null);
    setSuccess(null);
  };

  // ========== 權限檢查 ==========
  if (permissionLoading) {
    return <div className="page-container"><div className="loading">{t('common.loading')}</div></div>;
  }

  if (!hasPermission('section_b1', 'read')) {
    return (
      <div className="page-container">
        <div className="error-message">{t('common.noPermission')}</div>
      </div>
    );
  }

  const canEdit = hasPermission('section_b1', 'update');

  // ========== 渲染 ==========
  return (
    <div className="page-container">
      <div className="page-header">
        <FunctionPageHeader funcCode="section_b1" />
      </div>

      {error && <div className="error-message" style={{ marginBottom: '16px' }}>{error}</div>}
      {success && <div className="success-message" style={{ marginBottom: '16px' }}>{success}</div>}

      {/* 編輯表單 */}
      {selectedProjectId && (
        <div style={{ marginBottom: '24px' }}>
          {loading ? (
            <div className="loading">{t('common.loading')}</div>
          ) : (
            <>
              {/* 引用說明 */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                  {i18n.language === 'zh-TW' ? '引用說明' : 'Method References'}
                </label>
                <RichTextEditor
                  value={formData.mothed_references}
                  onChange={(html) => setFormData({ ...formData, mothed_references: html })}
                  placeholder={i18n.language === 'zh-TW' ? '請輸入方法論引用說明...' : 'Please enter method references...'}
                  disabled={!canEdit}
                />
              </div>

              {/* 儲存/重置按鈕 */}
              {canEdit && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
                  <button
                    className="btn-primary"
                    onClick={handleSave}
                    disabled={loading}
                  >
                    {loading ? t('message.saving') : t('common.save')}
                  </button>
                  <button
                    className="btn-secondary"
                    onClick={handleReset}
                    disabled={loading}
                  >
                    {t('common.reset')}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {!selectedProjectId && !loading && (
        <div style={{
          padding: '48px',
          textAlign: 'center',
          color: '#999',
          fontSize: '16px'
        }}>
          {i18n.language === 'zh-TW' ? '請先在上方選擇專案' : 'Please select a project from the top bar'}
        </div>
      )}
    </div>
  );
};

export default SectionB1Page;
